// Crypto primitives, ported 1:1 from RustDesk's web client (globals.js) so the
// wire behaviour is byte-identical to the native/controlled side.
//
//   verify  -> crypto_sign_open (Ed25519): open a signed blob with a sign pubkey
//   seal    -> crypto_box_easy  (X25519 + XSalsa20-Poly1305), zero nonce
//   encrypt -> crypto_secretbox_easy      (XSalsa20-Poly1305), counter nonce
//   decrypt -> crypto_secretbox_open_easy
//
// Session messages use a monotonically increasing counter as the 24-byte nonce
// (little-endian), with independent send/recv counters incremented BEFORE use.

import _sodium from "libsodium-wrappers";

let sodium: typeof _sodium | undefined;

export async function initCrypto(): Promise<void> {
  if (!sodium) {
    await _sodium.ready;
    sodium = _sodium;
  }
}

function s(): typeof _sodium {
  if (!sodium) throw new Error("crypto not initialised — call initCrypto() first");
  return sodium;
}

export function decodeBase64(b64: string): Uint8Array {
  return s().from_base64(b64, s().base64_variants.ORIGINAL);
}

/** Open an Ed25519-signed blob; throws if the signature does not verify. */
export function verify(signed: Uint8Array, pk: Uint8Array | string): Uint8Array {
  const key = typeof pk === "string" ? decodeBase64(pk) : pk;
  return s().crypto_sign_open(signed, key);
}

/** Fresh X25519 box keypair -> [secretKey, publicKey]. */
export function genBoxKeyPair(): [Uint8Array, Uint8Array] {
  const pair = s().crypto_box_keypair();
  return [pair.privateKey, pair.publicKey];
}

/** Fresh 32-byte symmetric (secretbox) key. */
export function genSecretKey(): Uint8Array {
  return s().crypto_secretbox_keygen();
}

/** Seal `data` to peer's box pubkey with our box secret, using an all-zero nonce. */
export function seal(
  data: Uint8Array,
  theirPk: Uint8Array,
  ourSk: Uint8Array
): Uint8Array {
  const nonce = new Uint8Array(24); // 24 zero bytes, matches RustDesk
  return s().crypto_box_easy(data, nonce, theirPk, ourSk);
}

/** 24-byte little-endian nonce from a counter (RustDesk's makeOnce). */
export function counterNonce(value: number): Uint8Array {
  const bytes = new Uint8Array(24);
  for (let i = 0; i < bytes.length && value > 0; i++) {
    const b = value & 0xff;
    bytes[i] = b;
    value = (value - b) / 256;
  }
  return bytes;
}

export function encrypt(data: Uint8Array, counter: number, key: Uint8Array): Uint8Array {
  return s().crypto_secretbox_easy(data, counterNonce(counter), key);
}

export function decrypt(data: Uint8Array, counter: number, key: Uint8Array): Uint8Array {
  return s().crypto_secretbox_open_easy(data, counterNonce(counter), key);
}
